import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import "leaflet/dist/leaflet.css"

/**
 * Map controller for displaying address validation results with bounds
 */
export default class extends Controller {
  static values = {
    latitude: Number,
    longitude: Number,
    bounds: String, // Changed to String to handle JSON
    featureSize: Number, // Size in meters from geocode data
    zoom: { type: Number, default: 15 }
  }

  connect() {
    // Create map only after the element is visible to ensure proper rendering
    if (this.element.offsetParent !== null) {
      this.initializeMap()
    } else {
      // Set up a mutation observer to detect when the element becomes visible
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.target.style.display !== 'none' && !this.map) {
            this.initializeMap()
          }
        })
      })
      
      // Start observing style changes on parent elements
      let parent = this.element.closest('.document-app__indicator-content')
      if (parent) {
        this.observer.observe(parent, { attributes: true, attributeFilter: ['style'] })
      }
    }
  }
  
  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
    
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initializeMap() {
    if (this.map) return // Don't initialize twice
    
    // Fix Leaflet's icon path issues
    this.fixLeafletIcons()
    
    // Initialize the map
    this.map = L.map(this.element, {
      center: [this.latitudeValue, this.longitudeValue],
      zoom: this.zoomValue,
      scrollWheelZoom: false,
      dragging: true,
      zoomControl: true
    })

    // Add OpenStreetMap tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map)

    // Add the main location marker
    const marker = L.marker([this.latitudeValue, this.longitudeValue]).addTo(this.map)
    marker.bindPopup(`${this.latitudeValue.toFixed(6)}, ${this.longitudeValue.toFixed(6)}`)

    // Add bounds rectangle if available
    if (this.boundsValue) {
      try {
        // Parse the JSON string if needed
        const boundsData = typeof this.boundsValue === 'string' ? JSON.parse(this.boundsValue) : this.boundsValue
        
        if (boundsData && boundsData.high && boundsData.low) {
          const lowLat = boundsData.low.latitude
          const lowLng = boundsData.low.longitude
          const highLat = boundsData.high.latitude
          const highLng = boundsData.high.longitude
          
          // Check if bounds are actually different from each other
          const latDiff = Math.abs(highLat - lowLat)
          const lngDiff = Math.abs(highLng - lowLng)
          
          if (latDiff > 0.00001 || lngDiff > 0.00001) {
            // Bounds are different, show rectangle
            const bounds = [
              [lowLat, lowLng],
              [highLat, highLng]
            ]
            
            // Create a rectangle for the bounds
            const rectangle = L.rectangle(bounds, {
              color: '#ff4444',
              weight: 3,
              opacity: 0.7,
              fillColor: '#ff4444',
              fillOpacity: 0.2,
              dashArray: '5, 5'
            }).addTo(this.map)
            
            // Add popup to the rectangle
            rectangle.bindPopup('Address validation bounds')
            
            // Fit the map to show both the marker and the bounds
            const group = new L.featureGroup([marker, rectangle])
            this.map.fitBounds(group.getBounds().pad(0.1))
          } else {
            // Bounds are the same point, show a circle instead
            // Use feature size if available, otherwise default to 50 meters
            const radius = this.featureSizeValue || 50
            
            const circle = L.circle([this.latitudeValue, this.longitudeValue], {
              color: '#ff4444',
              weight: 3,
              opacity: 0.7,
              fillColor: '#ff4444',
              fillOpacity: 0.2,
              radius: radius
            }).addTo(this.map)
            
            circle.bindPopup(`Address validation area (${radius}m radius)`)
            
            // Adjust zoom based on radius size
            let zoomLevel = 17
            if (radius > 100) zoomLevel = 16
            if (radius > 500) zoomLevel = 15
            if (radius > 1000) zoomLevel = 14
            
            this.map.setView([this.latitudeValue, this.longitudeValue], zoomLevel)
          }
        } else {
          // If no bounds, just center on the marker
          this.map.setView([this.latitudeValue, this.longitudeValue], this.zoomValue)
        }
      } catch (error) {
        console.error('Error parsing bounds data:', error)
        // Fallback to just centering on the marker
        this.map.setView([this.latitudeValue, this.longitudeValue], this.zoomValue)
      }
    } else {
      // No bounds data, just center on the marker
      this.map.setView([this.latitudeValue, this.longitudeValue], this.zoomValue)
    }
    
    // Fix map size issues after initial render
    setTimeout(() => {
      this.map.invalidateSize()
    }, 100)
  }
  
  // Fix Leaflet icon path issues (common problem in Rails/webpack environments)
  fixLeafletIcons() {
    delete L.Icon.Default.prototype._getIconUrl
    
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png'
    })
  }
}